import React, { useEffect, useState } from "react";
import { PageHeader, DataTable } from "@/components/common";
import { api, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip as UiTooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Download, HelpCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";

const fmt = new Intl.NumberFormat("tr-TR");
const pctFmt = new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const pct = (n) => `${pctFmt.format(n || 0)}%`;
const dISO = (d) => d.toISOString().slice(0, 10);
const today = new Date();
const days30ago = new Date(Date.now() - 29 * 86400000);

/** Header cell with question-mark tooltip for column definitions. */
function HeaderTip({ label, tip, testid }) {
  return (
    <TooltipProvider delayDuration={100}>
      <UiTooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex items-center gap-1 cursor-help select-none" data-testid={testid}>
            {label}
            <HelpCircle className="w-3 h-3 text-slate-400" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-[11px] leading-relaxed">
          {tip}
        </TooltipContent>
      </UiTooltip>
    </TooltipProvider>
  );
}

export default function ReportsPage() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [start, setStart] = useState(dISO(days30ago));
  const [end, setEnd] = useState(dISO(today));
  const [muns, setMuns] = useState([]);
  const [munFilter, setMunFilter] = useState("all");
  const [tab, setTab] = useState("by-site");
  const [data, setData] = useState([]);
  const [daily, setDaily] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeConfirm, setPurgeConfirm] = useState("");
  const [purging, setPurging] = useState(false);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/municipalities"); setMuns(data); } catch (_) { /* ignore */ }
  })(); }, []);

  async function loadReport() {
    setLoading(true);
    try {
      const params = { start, end };
      if (munFilter && munFilter !== "all") params.municipality_id = munFilter;
      const [rep, sum] = await Promise.all([
        api.get(`/admin/reports/${tab}`, { params }),
        api.get(`/admin/reports/summary`, { params: { start, end } }),
      ]);
      setData(rep.data);
      setDaily(sum.data.daily || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { loadReport(); }, [tab, start, end, munFilter]);

  function downloadFile(fmtKind) {
    const params = new URLSearchParams({ kind: tab, fmt: fmtKind, start, end });
    if (munFilter && munFilter !== "all") params.set("municipality_id", munFilter);
    const url = `${API}/admin/reports/export?${params.toString()}`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` }, credentials: "include" })
      .then((r) => {
        if (!r.ok) throw new Error(r.statusText);
        // Prefer server-provided turkified filename from Content-Disposition
        const cd = r.headers.get("Content-Disposition") || "";
        const m = /filename="?([^";]+)"?/i.exec(cd);
        const serverName = m ? m[1] : null;
        return r.blob().then((blob) => ({ blob, serverName }));
      })
      .then(({ blob, serverName }) => {
        const a = document.createElement("a");
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl;
        a.download = serverName || `${tab}_${start}_${end}.${fmtKind === "xlsx" ? "xlsx" : fmtKind}`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
        toast.success(`${fmtKind.toUpperCase()} indirildi`);
      })
      .catch((e) => toast.error(`İndirme hatası: ${e.message}`));
  }

  async function handlePurge() {
    if (purgeConfirm !== "TEMIZLE") {
      toast.error("Onay için TEMIZLE yazmanız gerekiyor");
      return;
    }
    setPurging(true);
    try {
      const params = { start, end, confirm: "TEMIZLE" };
      if (munFilter && munFilter !== "all") params.municipality_id = munFilter;
      const { data: res } = await api.delete("/admin/reports/purge", { params });
      toast.success(`${res.deleted} kayıt silindi (${res.range.start} → ${res.range.end})`);
      setPurgeOpen(false);
      setPurgeConfirm("");
      loadReport();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Bilinmeyen hata";
      toast.error(`Temizleme başarısız: ${msg}`);
    } finally {
      setPurging(false);
    }
  }

  const IMP_TIP = "Reklam görselinin yayıncı sitede tarayıcıya yüklendiği toplam sayı. Her sayfa yenilemesi ayrı bir gösterim sayılır.";
  const CLK_TIP = "Reklama yapılan gerçek tıklama sayısı. Aynı kullanıcının kısa sürede tekrar tıklamaları filtrelenir.";
  const CTR_TIP = "Tıklama Oranı (Click-Through Rate) = Tıklama / Gösterim × 100. Kampanyanın performans ölçüsüdür.";

  const tabConfig = {
    "by-site": {
      label: "Site Bazlı",
      defaultSort: { key: "impressions", dir: "desc" },
      cols: [
        { label: "Yayıncı Sitesi", sortKey: "site", sortValue: (r) => r.site_name || "", render: (r) => <span className="font-medium">{r.site_name}</span> },
        { label: "Alan Adı", sortKey: "domain", sortValue: (r) => r.site_domain || "", render: (r) => <span className="text-xs text-slate-500 font-mono">{r.site_domain}</span> },
        { label: "Belediye", sortKey: "mun", sortValue: (r) => r.municipality_name || "", render: (r) => r.municipality_name },
        { label: <HeaderTip label="Gösterim" tip={IMP_TIP} testid="tip-impressions" />, sortKey: "impressions", sortValue: (r) => r.impressions || 0, render: (r) => fmt.format(r.impressions) },
        { label: <HeaderTip label="Tıklama" tip={CLK_TIP} testid="tip-clicks" />, sortKey: "clicks", sortValue: (r) => r.clicks || 0, render: (r) => fmt.format(r.clicks) },
        { label: <HeaderTip label="TO %" tip={CTR_TIP} testid="tip-ctr" />, sortKey: "ctr", sortValue: (r) => r.ctr || 0, render: (r) => pct(r.ctr) },
      ],
    },
    "by-municipality": {
      label: "Belediye Bazlı",
      defaultSort: { key: "impressions", dir: "desc" },
      cols: [
        { label: "Belediye", sortKey: "mun", sortValue: (r) => r.municipality_name || "", render: (r) => <span className="font-medium">{r.municipality_name}</span> },
        { label: <HeaderTip label="Gösterim" tip={IMP_TIP} />, sortKey: "impressions", sortValue: (r) => r.impressions || 0, render: (r) => fmt.format(r.impressions) },
        { label: <HeaderTip label="Tıklama" tip={CLK_TIP} />, sortKey: "clicks", sortValue: (r) => r.clicks || 0, render: (r) => fmt.format(r.clicks) },
        { label: <HeaderTip label="TO %" tip={CTR_TIP} />, sortKey: "ctr", sortValue: (r) => r.ctr || 0, render: (r) => pct(r.ctr) },
      ],
    },
    "by-campaign": {
      label: "Kampanya Bazlı",
      defaultSort: { key: "impressions", dir: "desc" },
      cols: [
        { label: "Kampanya", sortKey: "campaign", sortValue: (r) => r.campaign_name || "", render: (r) => <div><div className="font-medium">{r.campaign_name}</div><div className="text-xs text-slate-500">{r.municipality_name}</div></div> },
        { label: <HeaderTip label="Gösterim" tip={IMP_TIP} />, sortKey: "impressions", sortValue: (r) => r.impressions || 0, render: (r) => fmt.format(r.impressions) },
        { label: <HeaderTip label="Tıklama" tip={CLK_TIP} />, sortKey: "clicks", sortValue: (r) => r.clicks || 0, render: (r) => fmt.format(r.clicks) },
        { label: <HeaderTip label="TO %" tip={CTR_TIP} />, sortKey: "ctr", sortValue: (r) => r.ctr || 0, render: (r) => pct(r.ctr) },
      ],
    },
    "by-size": {
      label: "Ölçü Bazlı",
      defaultSort: { key: "impressions", dir: "desc" },
      cols: [
        { label: "Ölçü", sortKey: "size", sortValue: (r) => r.ad_size_name || "", render: (r) => <span className="font-mono">{r.ad_size_name}</span> },
        { label: <HeaderTip label="Gösterim" tip={IMP_TIP} />, sortKey: "impressions", sortValue: (r) => r.impressions || 0, render: (r) => fmt.format(r.impressions) },
        { label: <HeaderTip label="Tıklama" tip={CLK_TIP} />, sortKey: "clicks", sortValue: (r) => r.clicks || 0, render: (r) => fmt.format(r.clicks) },
        { label: <HeaderTip label="TO %" tip={CTR_TIP} />, sortKey: "ctr", sortValue: (r) => r.ctr || 0, render: (r) => pct(r.ctr) },
      ],
    },
    "by-creative": {
      label: "Kreatif Bazlı",
      defaultSort: { key: "impressions", dir: "desc" },
      cols: [
        { label: "Kreatif", sortKey: "creative", sortValue: (r) => r.creative_alt || r.creative_id || "", render: (r) => <span className="font-medium">{r.creative_alt || r.creative_id}</span> },
        { label: "Ölçü", sortKey: "size", sortValue: (r) => r.ad_size_name || "", render: (r) => <span className="font-mono text-xs">{r.ad_size_name}</span> },
        { label: <HeaderTip label="Gösterim" tip={IMP_TIP} />, sortKey: "impressions", sortValue: (r) => r.impressions || 0, render: (r) => fmt.format(r.impressions) },
        { label: <HeaderTip label="Tıklama" tip={CLK_TIP} />, sortKey: "clicks", sortValue: (r) => r.clicks || 0, render: (r) => fmt.format(r.clicks) },
        { label: <HeaderTip label="TO %" tip={CTR_TIP} />, sortKey: "ctr", sortValue: (r) => r.ctr || 0, render: (r) => pct(r.ctr) },
      ],
    },
  };

  const dailyChart = (daily || []).map((d) => ({ date: d.date.slice(5), Gösterim: d.impressions, Tıklama: d.clicks }));

  return (
    <div className="space-y-6">
      <PageHeader
        overline="Analiz"
        title="Detaylı Raporlar"
        description="Tarih aralığı ve kırılım seçerek performansı analiz edin ve dışa aktarın."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => downloadFile("csv")} className="rounded-sm h-10" data-testid="export-csv-btn"><Download className="w-4 h-4 mr-1.5" /> CSV</Button>
            <Button variant="outline" onClick={() => downloadFile("xlsx")} className="rounded-sm h-10" data-testid="export-xlsx-btn"><Download className="w-4 h-4 mr-1.5" /> Excel</Button>
            <Button variant="outline" onClick={() => downloadFile("pdf")} className="rounded-sm h-10" data-testid="export-pdf-btn"><Download className="w-4 h-4 mr-1.5" /> PDF</Button>
            {isSuper && (
              <Button
                variant="outline"
                onClick={() => { setPurgeConfirm(""); setPurgeOpen(true); }}
                className="rounded-sm h-10 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                data-testid="reports-purge-btn"
              >
                <Trash2 className="w-4 h-4 mr-1.5" /> Temizle
              </Button>
            )}
          </div>
        }
      />

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div><Label className="text-xs">Başlangıç</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="rep-start" /></div>
        <div><Label className="text-xs">Bitiş</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="rep-end" /></div>
        <div>
          <Label className="text-xs">Belediye</Label>
          <Select value={munFilter} onValueChange={setMunFilter}>
            <SelectTrigger data-testid="rep-mun-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              {muns.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end"><div className="text-xs text-slate-500">{data.length} kayıt</div></div>
      </div>

      {/* Chart */}
      <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="reports-chart">
        <div className="text-[11px] uppercase tracking-[0.15em] text-slate-500 mb-3">Günlük Trend</div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dailyChart}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Gösterim" fill="#0f172a" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Tıklama" fill="#dc2626" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Data tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="report-tabs">
          {Object.entries(tabConfig).map(([k, v]) => <TabsTrigger key={k} value={k} data-testid={`report-tab-${k}`}>{v.label}</TabsTrigger>)}
        </TabsList>
        {Object.entries(tabConfig).map(([k, v]) => (
          <TabsContent key={k} value={k}>
            {loading ? <div className="text-sm text-slate-500">Yükleniyor…</div> : (
              <DataTable testid={`report-table-${k}`} columns={v.cols} rows={data} defaultSort={v.defaultSort} empty="Bu aralıkta veri yok." />
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Purge confirmation dialog */}
      <Dialog open={purgeOpen} onOpenChange={(v) => { if (!purging) setPurgeOpen(v); }}>
        <DialogContent data-testid="reports-purge-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700">Raporları Temizle</DialogTitle>
            <DialogDescription>
              Bu işlem <strong>{start}</strong> → <strong>{end}</strong> tarih aralığındaki tüm rapor kayıtlarını
              {munFilter !== "all" ? (<> (<strong>{muns.find(m => m.id === munFilter)?.name || "seçili belediye"}</strong>)</>) : (<> (<strong>tüm belediyeler</strong>)</>)} kalıcı olarak siler ve geri alınamaz.
              Ham olay kayıtları (`ad_events_raw`) 7 gün TTL ile zaten kendiliğinden silinir; burada yalnız birleştirilmiş rapor satırları (`ad_stats_hourly`) hedef alınır.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label className="text-xs">Onaylamak için aşağıya <span className="font-mono font-bold">TEMIZLE</span> yazın</Label>
            <Input
              value={purgeConfirm}
              onChange={(e) => setPurgeConfirm(e.target.value)}
              placeholder="TEMIZLE"
              autoFocus
              disabled={purging}
              data-testid="reports-purge-confirm-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPurgeOpen(false)} disabled={purging} data-testid="reports-purge-cancel-btn">Vazgeç</Button>
            <Button
              onClick={handlePurge}
              disabled={purgeConfirm !== "TEMIZLE" || purging}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="reports-purge-confirm-btn"
            >
              {purging ? "Siliniyor…" : "Kalıcı Sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
